/**
 * Upmix Module — honest stereo → 5.1 / 7.1 matrix upmix, shared by all tools.
 *
 * WHY THIS EXISTS
 * ---------------
 * Asking an encoder for more channels than the source has (`-ac 6`) produces a
 * file LABELLED 5.1 whose centre, LFE and both surrounds are digital silence —
 * measured on the shipped WASM build: 4 of 6 channels at -inf dB. That fake was
 * banned 2026-07-21 (the channel twin of upscaling), which is why ChannelModule
 * disables surround options a stereo source cannot fill.
 *
 * This module is the legitimate alternative: a matrix upmix in the spirit of
 * Dolby Pro Logic II, which DERIVES the extra channels from the stereo content
 * instead of inventing silence.
 *
 *   mid  = (L+R)/2   — what both channels share (vocals, bass, dialogue)
 *   side = (L-R)/2   — what they differ by (reverb, width, ambience)
 *
 *   FC  = mid                     the correlated anchor
 *   FL  = 0.646*L - 0.354*R       centre content removed so it isn't doubled
 *   FR  = 0.646*R - 0.354*L         when the centre plays alongside the fronts
 *   LFE = lowpass(mid, 120 Hz)    a subwoofer must never get full-range audio
 *   BL  = bandpass(side) delayed  decorrelated ambience; the delay keeps the
 *   BR  = -BL (polarity flipped)    front image from collapsing into the rears
 *
 * 7.1 splits the ambience in two: sides arrive earlier and wider (8 ms,
 * 100–7000 Hz), backs later and darker (18 ms, 100–5000 Hz, -2 dB), so the two
 * pairs are not copies of each other.
 *
 * MEASURED on a controlled probe (60 Hz + 500 Hz in both channels, 1000 Hz left
 * only, 1500 Hz right only), 2026-07-23, in the shipped FFmpeg WASM build:
 *   LFE      60 Hz -28.8 dB, everything above it ≥26 dB down
 *   FC       carries the shared 500 Hz
 *   BL/BR    carry ONLY the unique tones — shared content is 80+ dB down
 *   fronts   keep the stereo image (left-only tone 5 dB louder in FL than FR)
 *   ITU downmix back to stereo correlates 1.000 with the original
 *   no channel clips
 *
 * WHY filter_complex AND NOT A SIMPLE -af CHAIN
 * ---------------------------------------------
 * A one-line `-af` version needs per-channel biquads (`lowpass=...:c=LFE`).
 * Measured in our build: both the by-name and by-index forms mis-target — the
 * LFE kept its full range while the FRONTS lost their bass. So the graph form
 * is the only one that measures correct here. Tools that already have their own
 * `-af` chain pass it as `preFilter` and it is composed into the graph rather
 * than colliding with it.
 *
 * USAGE
 * -----
 *   FFmpeg-pipeline tools:
 *     var a = UpmixModule.ffmpegArgs(6, existingAfChainOrNull);
 *     args = codecArgs.concat(a);          // a already carries -filter_complex + -map
 *
 *   Web-Audio-pipeline tools (they hold an AudioBuffer):
 *     var out = await UpmixModule.processBuffer(buffer, 6);
 *
 * Offered ONLY for a 2-channel source. A mono source has no side signal at all,
 * so "upmixing" it would put silence in the surrounds — the very thing this
 * module exists to avoid. ChannelModule enforces that, and so does this file.
 */
(function () {
'use strict';

// ── Matrix constants — one source of truth for both pipelines ───────────────
var FRONT_DIRECT = 0.646;   // own channel kept in FL/FR
var FRONT_CROSS = 0.354;    // opposite channel subtracted (= centre removal)
var CENTRE_GAIN = 0.5;      // FC = 0.5*(L+R)
var LFE_GAIN = 0.5;         // LFE fed from mid at the same level, then lowpassed
var LFE_CUTOFF = 120;       // Hz — above this a subwoofer only muddies things
// FFmpeg's `lowpass` / `highpass` default to Q = 0.707. Web Audio takes the Q of
// those two filter types in dB, so the same filter is 20·log10(0.707) there.
var FF_Q_DB = 20 * Math.log10(0.707);   // −3.0116
var SUR_HP = 100;           // Hz — keep bass out of the rears
var SUR_LP_51 = 7000;       // Hz
var SUR_DELAY_51 = 12;      // ms — Haas: rears must lag the fronts
var SIDE_LP_71 = 7000, SIDE_DELAY_71 = 8;
var BACK_LP_71 = 5000, BACK_DELAY_71 = 18, BACK_GAIN_71 = 0.8;

function panFront(layout) {
    var zeros = layout === '7.1'
        ? '|LFE=0*c0|BL=0*c0|BR=0*c0|SL=0*c0|SR=0*c0'
        : '|LFE=0*c0|BL=0*c0|BR=0*c0';
    return 'pan=' + layout +
        '|FL=' + FRONT_DIRECT + '*c0-' + FRONT_CROSS + '*c1' +
        '|FR=' + FRONT_DIRECT + '*c1-' + FRONT_CROSS + '*c0' +
        '|FC=' + CENTRE_GAIN + '*c0+' + CENTRE_GAIN + '*c1' + zeros;
}
function panOnly(layout, assignments) {
    // Everything not listed is explicitly zeroed — `pan` leaves unnamed
    // channels silent anyway, but being explicit keeps the graphs greppable.
    var all = layout === '7.1'
        ? ['FL', 'FR', 'FC', 'LFE', 'BL', 'BR', 'SL', 'SR']
        : ['FL', 'FR', 'FC', 'LFE', 'BL', 'BR'];
    var named = Object.keys(assignments);
    var parts = named.map(function (k) { return k + '=' + assignments[k]; });
    all.forEach(function (k) { if (named.indexOf(k) === -1) parts.push(k + '=0*c0'); });
    return 'pan=' + layout + '|' + parts.join('|');
}
var MID = 'pan=mono|c0=' + LFE_GAIN + '*c0+' + LFE_GAIN + '*c1';
var SIDE = 'pan=stereo|c0=0.5*c0-0.5*c1|c1=0.5*c1-0.5*c0';

/**
 * Build the filter_complex graph.
 * @param {number} targetCh 6 or 8
 * @param {string} [preFilter] the tool's own simple -af chain, applied first
 * @param {string} [inputLabel] the graph's input stream label WITHOUT brackets,
 *   default '0:a' (input 0's audio). A multi-track extractor passes e.g. '0:3'
 *   to upmix a specific stream; a tool feeding a WAV/FLAC it built passes '0:a'.
 * @returns {string}
 */
function graph(targetCh, preFilter, inputLabel) {
    var pre = preFilter ? String(preFilter).replace(/^,+|,+$/g, '') + ',' : '';
    var IN = '[' + (inputLabel || '0:a') + ']';
    if (targetCh === 8) {
        return IN + pre + 'asplit=4[a1][a2][a3][a4];' +
            '[a1]' + panFront('7.1') + '[f];' +
            '[a2]' + MID + ',lowpass=f=' + LFE_CUTOFF + ',' + panOnly('7.1', { LFE: 'c0' }) + '[l];' +
            '[a3]' + SIDE + ',highpass=f=' + SUR_HP + ',lowpass=f=' + SIDE_LP_71 +
                ',adelay=' + SIDE_DELAY_71 + '|' + SIDE_DELAY_71 + ',' + panOnly('7.1', { SL: 'c0', SR: 'c1' }) + '[s];' +
            '[a4]' + SIDE + ',highpass=f=' + SUR_HP + ',lowpass=f=' + BACK_LP_71 +
                ',adelay=' + BACK_DELAY_71 + '|' + BACK_DELAY_71 + ',' +
                panOnly('7.1', { BL: BACK_GAIN_71 + '*c0', BR: BACK_GAIN_71 + '*c1' }) + '[b];' +
            '[f][l][s][b]amix=inputs=4:normalize=0:duration=first[out]';
    }
    return IN + pre + 'asplit=3[a1][a2][a3];' +
        '[a1]' + panFront('5.1') + '[f];' +
        '[a2]' + MID + ',lowpass=f=' + LFE_CUTOFF + ',' + panOnly('5.1', { LFE: 'c0' }) + '[l];' +
        '[a3]' + SIDE + ',highpass=f=' + SUR_HP + ',lowpass=f=' + SUR_LP_51 +
            ',adelay=' + SUR_DELAY_51 + '|' + SUR_DELAY_51 + ',' + panOnly('5.1', { BL: 'c0', BR: 'c1' }) + '[s];' +
        '[f][l][s]amix=inputs=3:normalize=0:duration=first[out]';
    // amix carries normalize=0 deliberately: the default rescales by input
    // count and would drop the whole mix ~10 dB (the trap already documented
    // for the video-audio merge). duration=first: the output ends with the
    // undelayed fronts — the source's length — not with the delayed rears.
}

window.UpmixModule = {
    /** Channel counts this module can synthesise. */
    TARGETS: [6, 8],

    /**
     * Is an upmix the right move for this pair? Stereo source only — see the
     * mono note in the file header.
     * @param {number} targetCh desired output channels
     * @param {number} sourceCh channels the file actually has
     */
    isUpmix: function (targetCh, sourceCh) {
        return sourceCh === 2 && (targetCh === 6 || targetCh === 8);
    },

    /** The graph string, for callers that assemble their own args. */
    graph: graph,

    /**
     * Complete FFmpeg args for the upmix.
     * @param {number} targetCh 6 or 8
     * @param {string} [preFilter] the tool's existing simple -af chain, if any
     * @param {string} [inputLabel] graph input stream label without brackets
     *   (default '0:a'); a multi-track extractor passes e.g. '0:3'.
     * @returns {Array} ['-filter_complex', graph, '-map', '[out]']
     */
    ffmpegArgs: function (targetCh, preFilter, inputLabel) {
        if (targetCh !== 6 && targetCh !== 8) return [];
        return ['-filter_complex', graph(targetCh, preFilter, inputLabel), '-map', '[out]'];
    },

    /**
     * FFmpeg args for a VIDEO tool: the upmix graph feeds the audio, and the
     * original video stream is carried through alongside it. The caller still
     * appends its own video/audio codecs (e.g. '-c:v','libx264',...,'-c:a','aac').
     *
     * `videoFilter` MUST be handled here, not left as a separate `-vf`: ffmpeg
     * rejects `-vf` combined with `-filter_complex` on the same output (measured
     * — the file came out truncated). So a tool that scales/pads its video folds
     * that chain in as `[0:v]<videoFilter>[v]` and this maps `[v]`. With no
     * video filter it maps the raw `0:v?` (optional-stream form so an audio-only
     * input still works) and the caller copies the video (`-c:v copy`).
     * @param {number} targetCh 6 or 8
     * @param {string} [preFilter] the tool's existing simple -af chain
     * @param {string} [inputLabel] audio input label without brackets (default '0:a')
     * @param {string} [videoFilter] the tool's simple -vf chain (scale/pad/…), if any
     * @returns {Array} filter_complex + the two -map entries
     */
    ffmpegArgsWithVideo: function (targetCh, preFilter, inputLabel, videoFilter) {
        if (targetCh !== 6 && targetCh !== 8) return [];
        var audioGraph = graph(targetCh, preFilter, inputLabel);
        var vf = videoFilter ? String(videoFilter).replace(/^,+|,+$/g, '') : '';
        if (vf) {
            var full = '[0:v]' + vf + '[v];' + audioGraph;
            return ['-filter_complex', full, '-map', '[v]', '-map', '[out]'];
        }
        return ['-filter_complex', audioGraph, '-map', '0:v?', '-map', '[out]'];
    },

    /**
     * Web Audio implementation of the SAME matrix, for tools that render an
     * AudioBuffer themselves instead of shelling out to FFmpeg.
     * @param {AudioBuffer} buffer stereo source
     * @param {number} targetCh 6 or 8
     * @returns {Promise<AudioBuffer>} a targetCh-channel buffer
     */
    processBuffer: function (buffer, targetCh) {
        if (!buffer || (targetCh !== 6 && targetCh !== 8)) return Promise.resolve(buffer);
        if (buffer.numberOfChannels !== 2) return Promise.resolve(buffer);

        var sr = buffer.sampleRate;
        var OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        // The file keeps the SOURCE's length. The delayed rears used to run on
        // past the end (+12 ms for 5.1, +18 ms for 7.1), so an album exported
        // track by track grew a sliver of silence in the fronts at every
        // gapless transition, and the stems no longer lined up with the
        // stereo they came from (2026-10-09, «5.1 zur Weiterverarbeitung»).
        // What is cut is the last few ms of the delayed ambience copy. The
        // FFmpeg graph ends the same way (amix duration=first).
        var len = buffer.length;
        var ctx = new OfflineCtx(targetCh, len, sr);

        var src = ctx.createBufferSource();
        src.buffer = buffer;
        var split = ctx.createChannelSplitter(2);
        src.connect(split);

        var merger = ctx.createChannelMerger(targetCh);
        // Merger inputs follow the WAVE_FORMAT_EXTENSIBLE order that both
        // FFmpeg and the WAV writers use: FL FR FC LFE BL BR [SL SR].
        var FL = 0, FR = 1, FC = 2, LFE = 3, BL = 4, BR = 5, SL = 6, SR = 7;

        function gain(v) { var g = ctx.createGain(); g.gain.value = v; return g; }
        function wire(from, node, toInput) { from.connect(node); node.connect(merger, 0, toInput); }

        // Fronts — centre content subtracted so it does not play twice.
        var flDirect = gain(FRONT_DIRECT), flCross = gain(-FRONT_CROSS);
        split.connect(flDirect, 0); split.connect(flCross, 1);
        flDirect.connect(merger, 0, FL); flCross.connect(merger, 0, FL);
        var frDirect = gain(FRONT_DIRECT), frCross = gain(-FRONT_CROSS);
        split.connect(frDirect, 1); split.connect(frCross, 0);
        frDirect.connect(merger, 0, FR); frCross.connect(merger, 0, FR);

        // Centre = mid.
        var cL = gain(CENTRE_GAIN), cR = gain(CENTRE_GAIN);
        split.connect(cL, 0); split.connect(cR, 1);
        cL.connect(merger, 0, FC); cR.connect(merger, 0, FC);

        // LFE = lowpassed mid. ONE biquad with FFmpeg's default Q (0.707,
        // Butterworth, 12 dB/oct) — the same filter the graph() branch runs.
        // Cascading two measured 23 dB deeper at 500 Hz than the FFmpeg branch,
        // i.e. the same "5.1" would have had a different subwoofer depending on
        // which pipeline the tool used (parity test, 2026-07-23).
        // The Q goes in as FF_Q_DB, not as 0.707: Web Audio reads Q of a
        // lowpass/highpass in DECIBELS. `Q.value = 0.707` stood here until
        // 2026-10-07 and was a resonance, not Butterworth — the subwoofer came
        // out +3.7 dB hotter at 120 Hz than in the FFmpeg branch, and so did
        // both corners of every rear pair (measured on this function against
        // the graph() string in ffmpeg). The 2026-07-23 parity probe compared
        // 60 and 500 Hz, where the two differ by 1.4 and 0.3 dB, and passed.
        var lfeL = gain(LFE_GAIN), lfeR = gain(LFE_GAIN);
        var lp1 = ctx.createBiquadFilter();
        lp1.type = 'lowpass'; lp1.frequency.value = LFE_CUTOFF; lp1.Q.value = FF_Q_DB;
        split.connect(lfeL, 0); split.connect(lfeR, 1);
        lfeL.connect(lp1); lfeR.connect(lp1);
        lp1.connect(merger, 0, LFE);

        // Side signal — built once, reused by every ambience pair.
        var sideL = gain(0.5), sideR = gain(-0.5);
        split.connect(sideL, 0); split.connect(sideR, 1);
        var side = ctx.createGain();
        sideL.connect(side); sideR.connect(side);

        function ambience(lpHz, delayMs, level, leftInput, rightInput) {
            // FFmpeg's default Q on both, for the same reason as the LFE above.
            var hp = ctx.createBiquadFilter();
            hp.type = 'highpass'; hp.frequency.value = SUR_HP; hp.Q.value = FF_Q_DB;
            var lp = ctx.createBiquadFilter();
            lp.type = 'lowpass'; lp.frequency.value = lpHz; lp.Q.value = FF_Q_DB;
            var dl = ctx.createDelay(1); dl.delayTime.value = delayMs / 1000;
            side.connect(hp); hp.connect(lp); lp.connect(dl);
            wire(dl, gain(level), leftInput);
            // Opposite polarity on the right of the pair — the same trick the
            // FFmpeg branch gets from c1 = 0.5*R - 0.5*L.
            wire(dl, gain(-level), rightInput);
        }

        if (targetCh === 8) {
            ambience(SIDE_LP_71, SIDE_DELAY_71, 1, SL, SR);
            ambience(BACK_LP_71, BACK_DELAY_71, BACK_GAIN_71, BL, BR);
        } else {
            ambience(SUR_LP_51, SUR_DELAY_51, 1, BL, BR);
        }

        merger.connect(ctx.destination);
        src.start(0);
        return ctx.startRendering();
    }
};

// Drain calls captured by the boot stub (defer-onerror race defense).
if (typeof window.__drainSharedModule === 'function') window.__drainSharedModule('UpmixModule');

})();
